import { Fragment, ReactNode, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import { workflowStatusDisplay } from 'components/inboundReferrals/StatusBadge';
import CloseButton from 'components/shared/CloseButton';
import Foldout from 'components/shared/Foldout';
import { elapsed, simpleDateAtTime } from 'lib/date';
import { AdminRunStepDetail, AdminRunValue, AdminWorkflowRunDetail, WaitingCause } from 'models/AdminWorkflowRun';
import { WorkflowStatus } from 'models/Workflow';
import {
  adminWorkflowRunsQueryKeys,
  indexAdminWorkflowRunLogs,
  indexAdminWorkflowRunValues,
  showAdminWorkflowRun,
} from 'services/api/admin/workflowRuns';
import { colors } from 'styles/theme/colors';

import { attemptChains, stepDisplay, StepStatus, Subtitle, Title, Warned } from './runPresentation';

// One run, for someone trying to work out what happened to it.
export default function RunDetail() {
  const { id = '' } = useParams();
  const [tab, setTab] = useState<'steps' | 'values' | 'timeline'>('steps');

  const {
    data: run,
    isLoading,
    isError,
  } = useQuery({
    queryKey: adminWorkflowRunsQueryKeys.show(id),
    queryFn: ({ signal }) => showAdminWorkflowRun(id, signal),
  });

  if (isLoading) return <Page>Loading…</Page>;

  // Without this a mistyped or stale link sat on "Loading…" forever, because a failed query is
  // not loading and has no data. Support follows links from tickets, so this is a real path.
  if (isError || !run) {
    return (
      <Page>
        <Crumb to='/admin/workflows/runs'>Workflow Runs</Crumb>
        <Title>Run not found</Title>
        <Subtitle>No run with this id. It may have been removed, or the link may be incomplete.</Subtitle>
      </Page>
    );
  }

  return (
    <Page>
      <Crumb to='/admin/workflows/runs'>Workflow Runs</Crumb>
      <Title>{`${run.workflowName} · ${run.id.slice(0, 8)}`}</Title>

      <Facts>
        {/* First, so opening a run confirms you got the right person before you read anything
            else about it. */}
        <Fact label='Patient' value={run.patientName ?? '—'} />
        <Fact label='Client' value={run.clientName ?? '—'} />
        {/* The RUN's own status, so it uses the whole-run vocabulary the referral page uses
            ("Needs Attention"), not the per-step one the list's rollup column uses. */}
        <Fact label='Status' value={workflowStatusDisplay(run.status as WorkflowStatus, !!run.archivedAt).label} />
        <Fact label='Outcome' value={run.outcome === 'none' ? 'Undecided' : run.outcome} />
        <Fact
          label='Version'
          value={`v${run.version}`}
          // The published version as plain context, not a warning. Which contract a run is on is
          // worth knowing when you are reading its steps; how far back it is is not a problem.
          note={
            run.publishedVersion && run.publishedVersion !== run.version
              ? `current is v${run.publishedVersion}`
              : undefined
          }
        />
        <Fact label='Started' value={simpleDateAtTime(run.createdAt) ?? '—'} />
        <Fact
          label='Facilities'
          value={
            run.facilityNames.length > 3
              ? `${run.facilityNames.slice(0, 3).join(', ')} +${run.facilityNames.length - 3} more`
              : run.facilityNames.join(', ') || '—'
          }
        />
      </Facts>

      <WhyWaiting run={run} />

      <Tabs>
        {(['steps', 'values', 'timeline'] as const).map((name) => (
          <Tab key={name} type='button' $on={tab === name} onClick={() => setTab(name)}>
            {name[0].toUpperCase() + name.slice(1)}
          </Tab>
        ))}
      </Tabs>

      {tab === 'steps' && <Steps steps={run.steps} categories={run.categories} />}
      {tab === 'values' && <Values id={id} />}
      {tab === 'timeline' && <Timeline id={id} />}
    </Page>
  );
}

// The causes come from the API already grouped and ranked; this renders them and adds nothing.
// Deliberately no advice, no ownership and no next step: whether a value can be typed on the
// referral lives in this app's field configuration, not in engine data, and guessing at it sends
// people the wrong way.
function WhyWaiting({ run }: { run: AdminWorkflowRunDetail }) {
  // A stopped engine on its own is reason enough to show the panel: it is the most serious thing
  // the console can say, and with it out of the cause list there may be nothing else here.
  //
  // Keyed on dirtySince, not dirty. Evaluate returns on terminal runs before clearing the flag, so
  // a run that concludes while dirty carries it for good; the API nulls this for finished runs the
  // same way it does stuck_reason and the causes.
  if (!run.waiting?.length && !run.dirtySince) return null;

  return (
    <Panel>
      <PanelTitle>Why is this waiting?</PanelTitle>
      {run.dirtySince && <ProcessingStopped since={run.dirtySince} />}
      {groupByKind(run.waiting).map((group) =>
        // One of a kind reads better as the sentence it has always been. Several of a kind repeat
        // that sentence's clause verbatim, so the clause is hoisted into a heading and only the
        // subjects stay. Same rule the lanes already use: collapse when collapsing earns it.
        group.causes.length === 1 ? (
          <Cause key={group.causes[0].key}>
            <CauseTitle>{causeTitle(group.causes[0])}</CauseTitle>
            <CauseDetail cause={group.causes[0]} />
          </Cause>
        ) : (
          <Cause key={group.kind}>
            <CauseTitle>{KIND_HEADINGS[group.kind]}</CauseTitle>
            {group.causes.map((cause) => (
              <Grouped key={cause.key}>
                <GroupedSubject>{subjectPhrase(cause)}</GroupedSubject>
                <CauseDetail cause={cause} />
              </Grouped>
            ))}
          </Cause>
        )
      )}
    </Panel>
  );
}

// The one thing on this panel that means the engine itself failed, rather than the referral
// waiting on someone. Workflow#touch_dirty_and_evaluate sets the flag and enqueues
// Workflow::Evaluate, which clears it before reading anything, so a run still carrying it is one
// whose job never completed.
//
// Naming the worker is not the kind of remedy this console refuses to guess at: whether a value is
// typeable lives in field config the engine cannot see, but exactly one mechanism produces this.
function ProcessingStopped({ since }: { since: string }) {
  // A lower bound, hence "at least": `since` is the run's last write of ANY kind, and an ordinary
  // save bumps it without re-enqueueing, so an exact claim would understate the outage.
  const age = elapsed(new Date(since), new Date());

  return (
    <Stopped>
      <StoppedTitle>Processing has stopped for this referral</StoppedTitle>
      <Warned>
        {`Nothing has evaluated this referral for at least ${age}. `}
        Nothing here will move until something does, and everything below was worked out from state that was never
        re-checked.
      </Warned>
      <Subtitle>Engineering: check Sidekiq&rsquo;s retry and dead sets for Workflow::Evaluate on this run.</Subtitle>
    </Stopped>
  );
}

// No stored value names here. A grey slug under every line was most of what this panel showed.
// The pair they disambiguate is real (`payer_id` and `external_payer_id` are both "Payer ID"), so
// they live in each value's slideout on the Values tab instead.
function CauseDetail({ cause }: { cause: WaitingCause }) {
  return (
    <>
      {cause.lanes.length > 2 && <Subtitle>{cause.lanes.join(', ')}</Subtitle>}
      {cause.blocking.length > 0 && (
        <Subtitle>
          {`Holding up ${cause.blocking.length} other step${cause.blocking.length > 1 ? 's' : ''}: `}
          {cause.blocking.map(blockedName).join(', ')}
        </Subtitle>
      )}
    </>
  );
}

// Consecutive runs of one kind, not a regroup: the API has already ranked the causes in
// investigation order, so same-kind causes are adjacent and reordering here would throw that away.
function groupByKind(causes: WaitingCause[]) {
  const groups: { kind: WaitingCause['kind']; causes: WaitingCause[] }[] = [];

  causes.forEach((cause) => {
    const last = groups[groups.length - 1];
    if (last?.kind === cause.kind) last.causes.push(cause);
    else groups.push({ kind: cause.kind, causes: [cause] });
  });

  return groups;
}

// The clause causeTitle would otherwise repeat on every line of a group. Worded to match the list
// page's stuck labels where the two describe the same thing, so a person who filtered the list by
// "Started and never finished" meets the same words when they open a run.
const KIND_HEADINGS: Record<WaitingCause['kind'], string> = {
  errored: 'Steps that failed',
  value: 'Missing, and no step on this run produces them',
  running: 'Started and never finished',
  person: 'Waiting on a person',
  ready: 'Has everything it needs and has not run',
};

function laneSuffix(cause: WaitingCause) {
  if (cause.lanes.length === 0) return '';
  if (cause.lanes.length <= 2) return ` at ${cause.lanes.join(' and ')}`;

  return ` at ${cause.lanes.length} facilities`;
}

// What is left of a cause once its kind is the heading above it.
function subjectPhrase(cause: WaitingCause) {
  const labels = cause.subjects.map((s) => s.label);
  if (cause.kind === 'value') return joinWords(labels);

  return `${labels[0] ?? ''}${laneSuffix(cause)}`;
}

function causeTitle(cause: WaitingCause) {
  const where = laneSuffix(cause);
  const labels = cause.subjects.map((s) => s.label);
  const subject = labels[0] ?? '';

  if (cause.kind === 'value') {
    return labels.length === 1
      ? `${subject} is missing, and no step on this run produces it`
      : `${joinWords(labels)} are missing, and no step on this run produces them`;
  }
  if (cause.kind === 'errored') return `${subject} failed${where}`;
  // "has not finished", not "has not come back": the predicate behind this reads `status` and an
  // age, never `dispatched_ref`, so it cannot tell a lost integration callback from a step the
  // engine started and left. Callback language on a manual step like Confirm Referral Source
  // sends a reader looking for a vendor that was never involved.
  if (cause.kind === 'running') return `${subject} started and has not finished${where}`;
  if (cause.kind === 'person') return `${subject} is waiting on a person${where}`;
  if (cause.kind === 'ready') return `${subject} has not been recorded${where}`;

  return `${subject} is waiting${where}`;
}

// Same shape as the cause's own lanes: named at one or two facilities, counted beyond that.
function blockedName(entry: { label: string; lanes: string[] }) {
  if (entry.lanes.length === 0) return entry.label;
  if (entry.lanes.length <= 2) return `${entry.label} (${entry.lanes.join(' and ')})`;
  return `${entry.label} (${entry.lanes.length} facilities)`;
}

function joinWords(words: string[]) {
  return words.length < 2 ? (words[0] ?? '') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

// Every instance, current and retired. A retried step's earlier attempt is usually the story.
function Steps({ steps, categories }: { steps: AdminRunStepDetail[]; categories: { key: string; label: string }[] }) {
  const [openId, setOpenId] = useState<string | null>(null);

  const chains = attemptChains(steps);
  const labels = new Map(categories.map((category) => [category.key, category.label]));
  const open = chains.find((chain) => chain.latest.id === openId);
  let heading: string | null = null;

  return (
    <>
      <Table>
        <thead>
          <tr>
            <th>Step</th>
            <th>Status</th>
            <th>Started</th>
            <th>Finished</th>
          </tr>
        </thead>
        <tbody>
          {chains.map(({ latest, earlier }) => {
            const key = latest.categoryKey ?? 'ungrouped';
            const startsCategory = key !== heading;
            heading = key;
            const notes = [
              latest.lane,
              latest.stale ? 'out of date' : null,
              earlier.length > 0 ? `${earlier.length} earlier attempt${earlier.length > 1 ? 's' : ''}` : null,
            ].filter(Boolean);

            return (
              <Fragment key={latest.id}>
                {startsCategory && (
                  <tr>
                    <CategoryHeading colSpan={4}>{labels.get(key) ?? 'Other steps'}</CategoryHeading>
                  </tr>
                )}
                <Row
                  data-detail-row
                  $muted={!latest.current}
                  $on={latest.id === openId}
                  onClick={() => setOpenId(latest.id)}>
                  <td>
                    <div>{latest.actionLabel}</div>
                    {notes.length > 0 && <Subtitle>{notes.join(' · ')}</Subtitle>}
                  </td>
                  <td>
                    <StepStatus status={latest.status} />
                    {/* The pill shows the ENGINE status; several kinds present themselves
                        differently to the referral page, and this is where the two part company.
                        Through stepDisplay so it reads "Ready for Review" like the pill beside it,
                        rather than the raw `action_required`. */}
                    {latest.effectiveStatus !== latest.status && (
                      <Subtitle>{`shown as ${stepDisplay(latest.effectiveStatus).label}`}</Subtitle>
                    )}
                  </td>
                  <td>
                    <Subtitle>{latest.startedAt ? simpleDateAtTime(latest.startedAt) : '—'}</Subtitle>
                  </td>
                  <td>
                    <Subtitle>{latest.completedAt ? simpleDateAtTime(latest.completedAt) : '—'}</Subtitle>
                  </td>
                </Row>
              </Fragment>
            );
          })}
        </tbody>
      </Table>

      {open && (
        <DetailPanel
          title={open.latest.actionLabel}
          subtitle={open.latest.lane ?? undefined}
          onClose={() => setOpenId(null)}>
          {/* Keyed so the picker remounts per step. Without it the selected attempt carried
              across rows, and reopening a step showed whichever attempt you last looked at on a
              different one, with nothing on screen saying so. */}
          <StepAttempts key={open.latest.id} latest={open.latest} earlier={open.earlier} />
        </DetailPanel>
      )}
    </>
  );
}

// What happened on this attempt: its outcome, and when it settled if it did.
function attemptNote(attempt: AdminRunStepDetail) {
  const when = attempt.completedAt ?? attempt.startedAt;

  return `${stepDisplay(attempt.status).label}${when ? ` · ${simpleDateAtTime(when)}` : ''}`;
}

// The attempts of one step, newest first, with the current one selected. A re-run happens because
// something changed, so which attempt ran against what is usually the whole story — that is why
// each option carries its own outcome and date rather than just "attempt 2".
function StepAttempts({ latest, earlier }: { latest: AdminRunStepDetail; earlier: AdminRunStepDetail[] }) {
  const attempts = [latest, ...earlier];
  const [selectedId, setSelectedId] = useState(latest.id);
  const selected = attempts.find((attempt) => attempt.id === selectedId) ?? latest;

  return (
    <>
      {attempts.length > 1 && (
        <Attempts>
          {attempts.map((attempt, index) => (
            <Attempt
              key={attempt.id}
              type='button'
              $on={attempt.id === selectedId}
              onClick={() => setSelectedId(attempt.id)}>
              {index === 0 ? 'Latest' : `Attempt ${attempts.length - index}`}
              <AttemptNote>{attemptNote(attempt)}</AttemptNote>
            </Attempt>
          ))}
        </Attempts>
      )}
      <StepDetail step={selected} />
    </>
  );
}

function StepDetail({ step }: { step: AdminRunStepDetail }) {
  return (
    <Detail>
      <DetailBlock label='Information it used' body={step.inputs} empty='It did not need anything from the run.' />
      <DetailBlock label='What it recorded' body={step.outputs} empty='It did not record anything.' />
      {step.unobtainableValues.length > 0 && <Warned>{`Waiting on: ${step.unobtainableValues.join(', ')}`}</Warned>}
      {step.dispatchedRef && <Subtitle>{`Sent as ${step.dispatchedRef}`}</Subtitle>}
      {step.completedByName && <Subtitle>{`Completed by ${step.completedByName}`}</Subtitle>}
    </Detail>
  );
}

// `body` arrives as formatted JSON text from the API — see AdminRunStepDetail. "{}" is what an
// empty payload serialises to.
function DetailBlock({ label, body, empty }: { label: string; body: string; empty: string }) {
  const filled = !!body && body.trim() !== '{}';

  return (
    <div>
      <DetailLabel>{label}</DetailLabel>
      {filled ? <Pre>{body}</Pre> : <Subtitle>{empty}</Subtitle>}
    </div>
  );
}

// Every row, superseded included: which write won is most of what this answers.
function Values({ id }: { id: string }) {
  // Values and the timeline are fetched when their tab opens, and both are the unbounded half of
  // the page. Without this an in-flight request renders an empty table, which reads as "this run
  // recorded nothing" — the opposite of the answer, and indistinguishable from the real thing.
  const { data = [], isLoading } = useQuery({
    queryKey: adminWorkflowRunsQueryKeys.values(id),
    queryFn: ({ signal }) => indexAdminWorkflowRunValues(id, signal),
  });
  const [openKey, setOpenKey] = useState<string | null>(null);

  const groups = valueHistories(data);
  const open = groups.find((group) => group.key === openKey);

  if (isLoading) return <Subtitle>Loading…</Subtitle>;

  return (
    <>
      <Table>
        <thead>
          <tr>
            <th>Value</th>
            <th>Content</th>
            <th>Source</th>
            <th>Written by</th>
            <th>Recorded</th>
          </tr>
        </thead>
        <tbody>
          {groups.map(({ key, current, earlier }) => (
            <Row
              key={key}
              data-detail-row
              $muted={!current.current}
              $on={key === openKey}
              onClick={() => setOpenKey(key)}>
              <td>
                <div>{current.nameLabel}</div>
                {/* Scope and write count only. The stored name lives in this row's slideout: on a
                    run with 386 values a slug under every row is most of what you see. */}
                {/* A faded row has to say why. When no write here is current it is because
                    the member was archived, not because a newer write replaced them. */}
                <ValueNotes value={current} earlierCount={earlier.length} />
              </td>
              <td>
                <Clamped>{display(current.value)}</Clamped>
              </td>
              <td>{current.source}</td>
              <td>{writtenBy(current)}</td>
              <td>
                <Subtitle>{simpleDateAtTime(current.createdAt)}</Subtitle>
              </td>
            </Row>
          ))}
        </tbody>
      </Table>

      {open && (
        <DetailPanel
          title={open.current.nameLabel}
          subtitle={[open.current.name, open.current.scope].filter(Boolean).join(' · ')}
          onClose={() => setOpenKey(null)}>
          {[open.current, ...open.earlier].map((value, index) => (
            <Write key={value.id}>
              <DetailLabel>{index === 0 ? 'Current' : `Earlier write ${open.earlier.length - index + 1}`}</DetailLabel>
              <Pre>{display(value.value)}</Pre>
              <Subtitle>
                {[writtenBy(value), value.source, simpleDateAtTime(value.createdAt)].filter(Boolean).join(' · ')}
              </Subtitle>
            </Write>
          ))}
        </DetailPanel>
      )}
    </>
  );
}

function ValueNotes({ value, earlierCount }: { value: AdminRunValue; earlierCount: number }) {
  const retired = value.scopeArchived ? 'no longer in use' : 'no longer current';
  const notes = [
    value.scope,
    earlierCount > 0 ? `${earlierCount} earlier write${earlierCount > 1 ? 's' : ''}` : null,
    value.current ? null : retired,
  ].filter(Boolean);

  return notes.length > 0 ? <Subtitle>{notes.join(' · ')}</Subtitle> : null;
}

// Who wrote it, and WHICH run of that step: a fanned-out step writes one value per coverage or
// facility, so the step name alone names three different writes on a multi-coverage referral.
function writtenBy(value: AdminRunValue) {
  if (value.writtenByName) return value.writtenByName;
  if (!value.writtenByStepLabel) return '—';

  return value.writtenByScope ? `${value.writtenByStepLabel} (${value.writtenByScope})` : value.writtenByStepLabel;
}

function display(value: unknown) {
  return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
}

// One row per value PER SCOPE, newest first. Values are append-only, so a value edited three
// times is three rows of the same name — and on a faceted run the same name legitimately exists
// once per coverage, which is why the scope is part of the key rather than collapsed away.
type ValueHistory = { key: string; current: AdminRunValue; earlier: AdminRunValue[] };

function valueHistories(values: AdminRunValue[]): ValueHistory[] {
  const groups = new Map<string, AdminRunValue[]>();
  values.forEach((value) => {
    // On scopeId, not the label: two coverages can carry the same payer name, and so can a
    // removed one and the re-added one that replaced it, which merged their histories into one.
    const key = `${value.name}|${value.scopeId ?? ''}`;
    groups.set(key, [...(groups.get(key) ?? []), value]);
  });

  return [...groups.entries()].map(([key, rows]) => {
    // The API orders by name then created_at desc, so the live row is first unless every write
    // has been superseded — in which case the newest is still the one to lead with.
    const ordered = [...rows].sort(
      (a, b) => Number(b.current) - Number(a.current) || b.createdAt.localeCompare(a.createdAt)
    );
    const [current, ...earlier] = ordered;
    return { key, current, earlier };
  });
}

function Timeline({ id }: { id: string }) {
  const { data = [], isLoading } = useQuery({
    queryKey: adminWorkflowRunsQueryKeys.logs(id),
    queryFn: ({ signal }) => indexAdminWorkflowRunLogs(id, signal),
  });

  if (isLoading) return <Subtitle>Loading…</Subtitle>;

  return (
    <div>
      {data.map((log) => (
        <Entry key={log.id}>
          <EntryTitle>{log.summary}</EntryTitle>
          {log.detail && <Subtitle>{log.detail}</Subtitle>}
          <Subtitle>
            {[log.event, log.actorName ?? log.actorKind, simpleDateAtTime(log.createdAt), log.scope]
              .filter(Boolean)
              .join(' · ')}
          </Subtitle>
        </Entry>
      ))}
    </div>
  );
}

function Fact({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div>
      <FactLabel>{label}</FactLabel>
      <div>{value}</div>
      {/* Muted, not warned. The only note here is the published version, which is context. */}
      {note && <Subtitle>{note}</Subtitle>}
    </div>
  );
}

// A heading row inside the table rather than separate tables per category: one set of columns
// keeps Started/Finished aligned down the whole run, which is most of what this tab is read for.
// A filled band, not just bolder text. As a <th> this inherits the table's column-header styling
// (12px, black50), so anything subtler reads as a second row of column headers rather than as a
// divider between stages.
const CategoryHeading = styled.th`
  text-align: left;
  padding: 9px 14px;
  font-size: 12px;
  font-weight: 500;
  color: ${colors.black100};
  background-color: ${colors.black5};
  border-top: 1px solid ${colors.black10};
`;

// Non-modal on purpose: the table behind stays live, so moving to the next step or value is one
// click instead of close-then-click. `Foldout` is the app's slideout; this only opts out of its
// backdrop.
function DetailPanel({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);

  // This Foldout is non-modal, so there is no backdrop to catch a click and dismissing it meant
  // finding the X. Closing on any outside press EXCEPT one on another row: those swap what the
  // panel shows rather than dismissing it, which is the whole reason it is non-modal. Escape too,
  // because a panel that closes on an outside click and not on Escape is the odd one out.
  //
  // `mousedown`, not `click`: a press that starts inside the panel and drags out (selecting the
  // text of a payload, which is most of why anyone opens this) would otherwise close it on release.
  useEffect(() => {
    function dismiss(event: MouseEvent) {
      const target = event.target as HTMLElement | null;
      if (!target || panel.current?.contains(target) || target.closest('[data-detail-row]')) return;

      onClose();
    }

    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }

    document.addEventListener('mousedown', dismiss);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('mousedown', dismiss);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <Foldout open modal={false} width='min(92%, 560px)' setOpen={onClose}>
      <PanelContainer ref={panel}>
        <PanelHeader>
          <div>
            <PanelHeading>{title}</PanelHeading>
            {subtitle && <Subtitle>{subtitle}</Subtitle>}
          </div>
          <CloseButton onClick={onClose} />
        </PanelHeader>
        <PanelContent>{children}</PanelContent>
      </PanelContainer>
    </Foldout>
  );
}

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.black10};
`;

const PanelHeading = styled.h2`
  font-size: 16px;
  font-weight: 500;
  margin: 0;
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// A long JSON value would otherwise set the column width for the whole table.
const Clamped = styled.div`
  max-height: 60px;
  overflow: hidden;
  word-break: break-word;
`;

const Write = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Attempts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Attempt = styled.button<{ $on: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: left;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  background: ${({ $on }) => ($on ? colors.white : 'transparent')};
  border: 1px solid ${({ $on }) => ($on ? colors.blue100 : colors.black10)};
  color: ${colors.black100};
`;

const AttemptNote = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${colors.black50};
`;

const Crumb = styled(Link)`
  font-size: 12px;
  color: ${colors.blue100};
`;

// MainLayout's ContentContainer is `overflow-y: hidden; height: 100%`, so a page taller than the
// viewport is clipped with no scrollbar unless it scrolls itself. Same shape as
// useAdminListPage's PageContainer, which is why the list pages already work.
const Page = styled.div`
  height: 100%;
  overflow-y: auto;
  padding: 24px 28px 80px;
`;

const Facts = styled.div`
  display: flex;
  gap: 34px;
  flex-wrap: wrap;
  padding: 14px 0 18px;
  border-bottom: 1px solid ${colors.black10};
`;

const FactLabel = styled.div`
  font-size: 12px;
  color: ${colors.black75};
  margin-bottom: 4px;
`;

const Panel = styled.div`
  border: 1px solid ${colors.black10};
  border-left: 3px solid ${colors.orange100};
  border-radius: 4px;
  padding: 14px 16px;
  margin: 16px 0;
`;

// The loudest thing in the panel, because it names everything under it. 500 and not 600: the
// theme declares only 400 and FONT_WEIGHT_MEDIUM 500, and Special Gothic's next face up is 700,
// so a 600 silently renders bold. It separates from the cause headings below by SIZE instead.
const PanelTitle = styled.div`
  font-weight: 500;
  margin-bottom: 10px;
`;

// Set apart from the causes, not ranked among them: it is a statement about the whole run and it
// undermines everything below it, so it reads first and reads differently.
const Stopped = styled.div`
  background: ${colors.orange10};
  border-radius: 4px;
  padding: 10px 12px;
  margin-bottom: 12px;
`;

const StoppedTitle = styled.div`
  font-weight: 500;
  color: ${colors.orangeHover};
  margin-bottom: 3px;
`;

const Cause = styled.div`
  padding: 11px 0;
  border-bottom: 1px solid ${colors.black5};

  &:last-child {
    border-bottom: 0;
    padding-bottom: 0;
  }
`;

// 14px against the panel title's 16px. Each tier of this panel separates on a different axis,
// because the type scale only has 16/14/12 and the weights only 400/500, and leaning on one axis
// for all three collapses two of them together: size from the title, weight from the subjects
// below it, then size and colour again for the detail lines.
const CauseTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 3px;
`;

// One subject under a kind heading. Indented rather than bulleted or boxed: the heading above it
// is already doing the grouping, and a second device competing with the panel's own left rule
// makes three nested edges on one card.
const Grouped = styled.div`
  padding-left: 12px;
  margin-top: 7px;
`;

// Same size as the heading above it, one weight lighter. The weight is the only axis left here:
// dropping to 12px would put it level with the muted detail lines beneath it, which are a tier
// further down again.
const GroupedSubject = styled.div`
  font-size: 14px;
  font-weight: 400;
`;

const Tabs = styled.div`
  display: flex;
  border-bottom: 1px solid ${colors.black10};
  margin: 18px 0 0;
`;

const Tab = styled.button<{ $on: boolean }>`
  background: none;
  border: 0;
  padding: 10px 16px;
  font-size: 14px;
  cursor: pointer;
  color: ${({ $on }) => ($on ? colors.black100 : colors.black50)};
  font-weight: ${({ $on }) => ($on ? 600 : 400)};
  border-bottom: 2px solid ${({ $on }) => ($on ? colors.blue100 : 'transparent')};
  margin-bottom: -1px;
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  font-size: 14px;
  margin-top: 8px;

  th {
    text-align: left;
    padding: 10px 14px;
    font-size: 12px;
    color: ${colors.black50};
    border-bottom: 1px solid ${colors.black10};
  }

  td {
    padding: 12px 14px;
    border-bottom: 1px solid ${colors.black5};
    vertical-align: top;
  }
`;

// `$on` marks the row the open panel is showing, because the panel is non-modal and the table
// stays visible beside it — without it you lose your place after two clicks.
const Row = styled.tr<{ $muted?: boolean; $on?: boolean }>`
  cursor: pointer;
  opacity: ${({ $muted }) => ($muted ? 0.6 : 1)};
  background: ${({ $on }) => ($on ? colors.blue10 : 'transparent')};

  &:hover {
    background: ${({ $on }) => ($on ? colors.blue10 : colors.black3)};
  }
`;

const Detail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px 0 8px;
`;

// Uppercase with letter-spacing is a house pattern (11 files use it); 12px matches
// PatientDemographics, which is the nearest thing to this: labels over read-only detail.
const DetailLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${colors.black50};
  margin-bottom: 4px;
`;

const Pre = styled.pre`
  max-height: 320px;
  overflow: auto;
  background: ${colors.black3};
  border: 1px solid ${colors.black10};
  border-radius: 4px;
  padding: 10px 12px;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
`;

const Entry = styled.div`
  padding: 10px 0;
  border-bottom: 1px solid ${colors.black5};
`;

// Was a bare <strong>, which the browser renders at 700 — the same mistake as the unstyled
// heading. 500 is what the rest of admin uses, and matches the cause titles above it.
const EntryTitle = styled.div`
  font-weight: 500;
  margin-bottom: 2px;
`;
